import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { AlertCircle, RefreshCw, WifiOff, ServerCrash, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './alert';

/**
 * ErrorState - Displays an error message with optional retry action
 * @param {string} title - Error title
 * @param {string} message - Error description
 * @param {string} type - Error type: 'generic', 'network', 'server', 'permission'
 * @param {function} onRetry - Callback for retry action
 * @param {string} retryLabel - Custom retry button label
 */
export const ErrorState = ({
  title,
  message,
  type = 'generic',
  onRetry,
  retryLabel = "Try Again",
  className
}) => {
  const errorConfig = {
    generic: {
      icon: AlertCircle,
      defaultTitle: "Something went wrong",
      defaultMessage: "An error occurred while loading. Please try again.",
      iconColor: "text-destructive"
    },
    network: {
      icon: WifiOff,
      defaultTitle: "Connection error",
      defaultMessage: "Unable to connect. Please check your internet connection and try again.",
      iconColor: "text-amber-500"
    },
    server: {
      icon: ServerCrash,
      defaultTitle: "Server error",
      defaultMessage: "The server is temporarily unavailable. Please try again later.",
      iconColor: "text-destructive"
    },
    permission: {
      icon: ShieldAlert,
      defaultTitle: "Access denied",
      defaultMessage: "You don't have permission to view this content.",
      iconColor: "text-amber-500"
    }
  };

  const config = errorConfig[type] || errorConfig.generic;
  const IconComponent = config.icon;

  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center py-16 px-4 text-center",
        className
      )}
      data-testid="error-state"
    >
      <div className={cn("rounded-full bg-muted p-4 mb-4")}>
        <IconComponent className={cn("h-8 w-8", config.iconColor)} />
      </div>
      <h3 className="text-lg font-semibold mb-2">
        {title || config.defaultTitle}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {message || config.defaultMessage}
      </p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" data-testid="error-retry-button">
          <RefreshCw className="h-4 w-4 mr-2" />
          {retryLabel}
        </Button>
      )}
    </div>
  );
};

/**
 * InlineError - Compact inline error message
 * @param {string} message - Error message
 * @param {function} onRetry - Optional retry callback
 */
export const InlineError = ({
  message = "Failed to load data",
  onRetry,
  className
}) => {
  return (
    <Alert variant="destructive" className={cn("my-4", className)} data-testid="inline-error">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Error</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{message}</span>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="ml-4">
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};

/**
 * ErrorBoundaryFallback - Fallback UI for React Error Boundaries
 * @param {Error} error - The caught error
 * @param {function} resetErrorBoundary - Function to reset the error boundary
 */
export const ErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
  className
}) => {
  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center min-h-[400px] p-8 text-center",
        className
      )}
      data-testid="error-boundary-fallback"
    >
      <div className="rounded-full bg-destructive/10 p-4 mb-4">
        <ServerCrash className="h-10 w-10 text-destructive" />
      </div>
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-sm text-muted-foreground max-w-md mb-4">
        An unexpected error occurred. Our team has been notified.
      </p>
      {error?.message && (
        <code className="text-xs bg-muted px-3 py-2 rounded mb-6 max-w-md overflow-auto">
          {error.message}
        </code>
      )}
      {resetErrorBoundary && (
        <Button onClick={resetErrorBoundary}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  );
};

export default {
  ErrorState,
  InlineError,
  ErrorBoundaryFallback
};
